import { API_BASE } from './api.js';
import { printLine, updateInputDisplay } from './terminal.js';
import { getFilesForCurrentDir } from './fs.js';
import { contactWizard } from './wizard.js';
import { 
    CONFIG, 
    CSS_CLASSES, 
    DOM_SELECTORS, 
    API_ENDPOINTS, 
    FALLBACK_BLOG_SLUGS, 
    COMMANDS, 
    COMMAND_LIST, 
    THEME_LIST, 
    BLOG_SUBCOMMANDS, 
    BLOG_SUBCOMMANDS_LIST 
} from './constants.js';

export let cachedBlogSlugs = [];

export const commandList = COMMAND_LIST;

const THEMES = THEME_LIST;
const BLOG_SUB_CMDS = BLOG_SUBCOMMANDS_LIST;

/** @param {string[]} slugs */
export function setCachedBlogSlugs(slugs) {
    cachedBlogSlugs = slugs;
}

/**
 * Fetches blog slugs from the API and caches them for tab-completion.
 * Falls back to a hardcoded list if the request fails.
 *
 * @returns {Promise<string[]>}
 */
export async function fetchBlogSlugsForAutocomplete() {
    try {
        const response = await fetch(`${API_BASE}${API_ENDPOINTS.BLOGS}`);
        if (!response.ok) throw new Error();
        const blogs = await response.json();
        cachedBlogSlugs = blogs.map(b => b.id !== undefined ? `${b.id}-${b.slug}` : b.slug);
        return cachedBlogSlugs;
    } catch {
        // Server unavailable — use seeded slugs so autocomplete still works.
        cachedBlogSlugs = FALLBACK_BLOG_SLUGS;
        return cachedBlogSlugs;
    }
}

/** Handles a Tab keypress by completing the current token in the terminal input. */
export function handleTabComplete() {
    if (contactWizard.active) return;

    const terminalInput = document.getElementById(DOM_SELECTORS.TERMINAL_INPUT);
    if (!terminalInput) return;

    const rawVal = terminalInput.value;
    const endsWithSpace = rawVal.endsWith(CONFIG.SPACE);
    const trimmedVal = rawVal.trim();
    if (!trimmedVal) return;

    const parts = trimmedVal.split(/\s+/);
    const cmd = parts[0].toLowerCase();

    if (parts.length > 1 || (parts.length === 1 && endsWithSpace)) {
        // Completing an argument or subcommand
        if (cmd === COMMANDS.CD || cmd === COMMANDS.CAT) {
            const arg = endsWithSpace ? CONFIG.EMPTY : parts.slice(1).join(CONFIG.SPACE).toLowerCase();
            const matches = getFilesForCurrentDir().filter(f => f.toLowerCase().startsWith(arg));
            if (matches.length === 1) {
                terminalInput.value = `${cmd} ${matches[0]}`;
            } else if (matches.length > 1) {
                printLine(`\n${matches.join('   ')}`, CSS_CLASSES.COMMENT);
            }
        } else if (cmd === COMMANDS.THEME) {
            const arg = endsWithSpace ? CONFIG.EMPTY : parts.slice(1).join(CONFIG.SPACE).toLowerCase();
            const matches = THEMES.filter(t => t.startsWith(arg));
            if (matches.length === 1) {
                terminalInput.value = `${COMMANDS.THEME} ${matches[0]}`;
            } else if (matches.length > 1) {
                printLine(`\n${matches.join('   ')}`, CSS_CLASSES.COMMENT);
            }
        } else if (cmd === COMMANDS.BLOG) {
            _completeBlogArg(terminalInput, parts, endsWithSpace);
        }
        updateInputDisplay();
        return;
    }

    // Complete base commands
    const matches = commandList.filter(c => c.startsWith(trimmedVal.toLowerCase()));
    if (matches.length === 1) {
        terminalInput.value = matches[0] + CONFIG.SPACE; // Append space for convenience
    } else if (matches.length > 1) {
        printLine(`\n${matches.join('   ')}`, CSS_CLASSES.COMMENT);
    }
    updateInputDisplay();
}

/**
 * Handles tab-completion logic specifically for the `blog` command tree.
 *
 * @param {HTMLInputElement} input
 * @param {string[]} parts
 * @param {boolean} endsWithSpace
 */
function _completeBlogArg(input, parts, endsWithSpace) {
    if (parts.length === 1 && endsWithSpace) {
        // `blog [tab]` — show all subcommands
        printLine(`\n${BLOG_SUB_CMDS.join('   ')}`, CSS_CLASSES.COMMENT);
        return;
    }

    if (parts.length === 2 && !endsWithSpace) {
        // `blog vi[tab]` — complete subcommand
        const subCmd = parts[1].toLowerCase();
        const matches = BLOG_SUB_CMDS.filter(sc => sc.startsWith(subCmd));
        if (matches.length === 1) {
            input.value = `${COMMANDS.BLOG} ${matches[0]} `; // Trailing space after completed subcommand
        } else if (matches.length > 1) {
            printLine(`\n${matches.join('   ')}`, CSS_CLASSES.COMMENT);
        }
        return;
    }

    const isViewSlugContext =
        (parts.length === 2 && endsWithSpace && parts[1].toLowerCase() === BLOG_SUBCOMMANDS.VIEW) ||
        (parts.length >= 3 && parts[1].toLowerCase() === BLOG_SUBCOMMANDS.VIEW);

    if (isViewSlugContext) {
        // `blog view ja[tab]` — complete slug
        const arg = (parts.length === 2 && endsWithSpace) ? CONFIG.EMPTY : parts.slice(2).join(CONFIG.SPACE).toLowerCase();
        const matches = cachedBlogSlugs.filter(slug => slug.toLowerCase().startsWith(arg));
        if (matches.length === 1) {
            input.value = `${COMMANDS.BLOG} ${BLOG_SUBCOMMANDS.VIEW} ${matches[0]}`;
        } else if (matches.length > 1) {
            printLine(`\n${matches.join('   ')}`, CSS_CLASSES.COMMENT);
        }
    }
}
